import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // main
  isSingleIndex: false,
  lastMultibleIndexInputs: [],
  lastSingleIndexInputs: [],
  addedIndexes: [],
  currentIndexId: null,
  // reference
  refToggle: false,
  uploadMaskToggle: false,
  RefIndex: [],
  selectedRef: {},
  rangePairs: [],
  values: [],
  refFromDate: {},
  refToDate: {},
  pinpointCoordinates: null,
};

const MonitoringSlice = createSlice({
  name: "monitoring",
  initialState,
  reducers: {
    setIsSingleIndex: (state, action) => {
      state.isSingleIndex = action.payload;
    },
    // main index
    setCurrentIndexId: (state, action) => {
      state.currentIndexId = action.payload;
    },

    setSelectedFromDate: (state, action) => {
      state.selectedFromDate = action.payload;
    },
    setSelectedToDate: (state, action) => {
      state.selectedToDate = action.payload;
    },

    setAddedIndexesInit: (state) => {
      const mainIndex = state.addedIndexes? state.addedIndexes[0] : null;
      state.addedIndexes = [
        ...state.addedIndexes,
        {
          uid: Date.now() + Math.random(),
          id: null,
          name: "",
          from_date: mainIndex?.from_date ? mainIndex.from_date : null,
          to_date: mainIndex?.to_date ? mainIndex.to_date : null,
          historicalYearFrom: mainIndex?.historicalYearFrom ? mainIndex.historicalYearFrom : null,
          historicalYearTo: mainIndex?.historicalYearTo ? mainIndex.historicalYearTo : null,
          filterToggle: mainIndex?.filterToggle ? mainIndex.filterToggle : false,
          order: mainIndex?.order ? mainIndex.order : "2",
          window: mainIndex?.window ? mainIndex.window : "10",
          periodOfTime: mainIndex?.periodOfTime ? mainIndex.periodOfTime : [
            {
              label: "By day",
              checked: true,
              key: "same",
            },
            {
              label: "10 Days",
              checked: false,
              key: "days",
            },
            {
              label: "Month",
              checked: false,
              key: "month",
            },
            {
              label: "Range",
              checked: false,
              key: "range",
            },
          ],
          temporalMethod: mainIndex?.temporalMethod ? mainIndex.temporalMethod : [
            {
              label: "By pixel",
              checked: false,
              key: "pixel",
            },
            {
              label: "By image",
              checked: true,
              key: "image",
            },
          ],
          summerizeOptions: mainIndex?.summerizeOptions ? mainIndex.summerizeOptions : [
            {
              label: "Mean",
              checked: true,
              key: "mean",
            },
            {
              label: "Median",
              checked: false,
              key: "median",
            },
            {
              label: "Max",
              checked: false,
              key: "max",
            },
            {
              label: "Min",
              checked: false,
              key: "min",
            },
            {
              label: "Sum",
              checked: false,
              key: "sum",
            },
          ],
          shortTermMonitoring: mainIndex?.shortTermMonitoring ? mainIndex.shortTermMonitoring : null,
        },
      ];
    },

    setAddIndexes: (state, action) => {
      state.addedIndexes = action.payload;
    },
    setLastMultibleIndexInputs: (state, action) => {
      state.lastMultibleIndexInputs = action.payload;
    },
    setLastSingleIndexInputs: (state, action) => {
      state.lastSingleIndexInputs = action.payload;
    },
    clearAddIndexes: (state) => {
      state.addedIndexes = [];
    },
    deleteOneAddIndex: (state, action) => {
      const { index } = action.payload;
      const updatedAddedIndexes = state.addedIndexes.filter(
        (_, i) => i !== index
      );

      state.addedIndexes = updatedAddedIndexes;
    },
    setAddedIndexesName: (state, action) => {
      const { index, name } = action.payload;

      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index] = {
        ...updatedIndexes[index],
        name: name,
      };

      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesId: (state, action) => {
      const { index, id } = action.payload;

      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index] = {
        ...updatedIndexes[index],
        id: id,
      };

      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesPeriodOfTime: (state, action) => {
      const { index, index2, periodOfTime } = action.payload;
      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index2] = {
        ...updatedIndexes[index2],
        periodOfTime: periodOfTime
          ? [...periodOfTime]
          : updatedIndexes[index2].periodOfTime.map((item, idx) => ({
              ...item,
              checked: idx === index,
            })),
      };

      state.addedIndexes = updatedIndexes;
    },

    setAddedIndexesSummerizeOptions: (state, action) => {
      const { index, index2, summerizeOptions } = action.payload;
      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index2] = {
        ...updatedIndexes[index2],
        summerizeOptions: summerizeOptions
          ? [...summerizeOptions]
          : updatedIndexes[index2].summerizeOptions.map((item, idx) => ({
              ...item,
              checked: idx === index,
            })),
      };

      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesTemporalMethod: (state, action) => {
      const { index, index2, temporalMethod } = action.payload;
      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index2] = {
        ...updatedIndexes[index2],
        temporalMethod: temporalMethod
          ? [...temporalMethod]
          : updatedIndexes[index2].temporalMethod.map((item, idx) => ({
              ...item,
              checked: idx === index,
            })),
      };

      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesOrder: (state, action) => {
      const { index, order } = action.payload;

      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index] = {
        ...updatedIndexes[index],
        order: order,
      };

      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesDerivative: (state, action) => {
      const { index, derivative } = action.payload;

      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index] = {
        ...updatedIndexes[index],
        derivative: derivative,
      };

      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesWindow: (state, action) => {
      const { index, window } = action.payload;

      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index] = {
        ...updatedIndexes[index],
        window: window,
      };

      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesShortTerm: (state, action) => {
      console.log("action.payload", action);
      const { index, short } = action.payload;
      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index] = {
        ...updatedIndexes[index],
        shortTermMonitoring: short,
      };

      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesFilterToggle: (state, action) => {
      const { index } = action.payload;

      const updatedIndexes = [...state.addedIndexes];
      console.log("state.addedIndexes", state.addedIndexes);

      updatedIndexes[index] = {
        ...updatedIndexes[index],
        filterToggle: !state.addedIndexes[index].filterToggle,
      };

      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesFilter: (state, action) => {
      const { index, filter } = action.payload;

      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index] = {
        ...updatedIndexes[index],
        filter: filter,
      };

      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesFromDate: (state, action) => {
      const { index, fromDate } = action.payload;

      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index] = {
        ...updatedIndexes[index],
        from_date: fromDate,
      };
      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesToDate: (state, action) => {
      const { index, toDate } = action.payload;

      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index] = {
        ...updatedIndexes[index],
        to_date: toDate,
      };
      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesYearTo: (state, action) => {
      console.log("action.payload/yearTo", action);
      const { index, yearTo } = action.payload;

      const updatedIndexes = [...state.addedIndexes];

      updatedIndexes[index] = {
        ...updatedIndexes[index],
        historicalYearTo: yearTo,
      };
      state.selectedYearTo = yearTo;
      state.addedIndexes = updatedIndexes;
    },
    setAddedIndexesYearFrom: (state, action) => {
      const { index, yearFrom } = action.payload;
      const updatedIndexes = [...state.addedIndexes];
      updatedIndexes[index] = {
        ...updatedIndexes[index],
        historicalYearFrom: yearFrom,
      };
      state.addedIndexes = updatedIndexes;
    },

    // reference index
    setRefIndex: (state, action) => {
      state.RefIndex = action.payload;
    },

    setRefFromDate: (state, action) => {
      state.refFromDate = action.payload;
    },
    setRefToDate: (state, action) => {
      state.refToDate = action.payload;
    },
    clearRefDates: (state) => {
      state.refFromDate = "";
      state.refToDate = "";
    },
    clearSelectedDates: (state) => {
      state.selectedFromDate = "";
      state.selectedToDate = "";
    },
    setPinpointCoordinates: (state, action) => {
      state.pinpointCoordinates = action.payload;
    },
    setSelectedRef: (state, action) => {
      const { index, path } = action.payload;
      console.log(path);
      state.selectedRef = {
        ...state.selectedRef,
        [state.RefIndex[index]]: [path],
      };
    },
    setRangePairs: (state, action) => {
      state.rangePairs = action.payload;
    },
    setValues: (state, action) => {
      state.values = action.payload;
    },
    setRefToggle: (state) => {
      state.refToggle = !state.refToggle;
    },
    setUploadMaskToggle: (state) => {
      state.uploadMaskToggle = !state.uploadMaskToggle;
    },
  },
  extraReducers: (builder) => {
    // Handle reset action
    builder.addCase("RESET", () => initialState);
  },
});

export const {
  setIsSingleIndex,
  setCurrentIndexId,
  setPinpointCoordinates,

  // main index
  setAddIndexes,
  setLastSingleIndexInputs,
  setLastMultibleIndexInputs,
  setAddedIndexesSummerizeOptions,
  setAddedIndexesPeriodOfTime,
  setAddedIndexesWindow,
  setAddedIndexesDerivative,
  setAddedIndexesOrder,
  setAddedIndexesFilter,
  setAddedIndexesFilterToggle,
  setAddedIndexesId,
  setAddedIndexesYearFrom,
  setAddedIndexesYearTo,
  setAddedIndexesInit,
  setAddedIndexesName,
  setAddedIndexesShortTerm,
  setAddedIndexesTemporalMethod,
  clearAddIndexes,
  deleteOneAddIndex,

  // ref index
  setValues,
  setRangePairs,
  setRefIndex,
  setSelectedRef,
  setRefToggle,
  setUploadMaskToggle,
  setRefFromDate,
  setRefToDate,
  setSelectedFromDate,
  setSelectedToDate,
  setAddedIndexesFromDate,
  setAddedIndexesToDate,
  clearRefDates,
  clearSelectedDates,
} = MonitoringSlice.actions;

export default MonitoringSlice.reducer;
